import { ChangeDetectorRef, Directive, EventEmitter, HostListener, inject, Input, Output } from "@angular/core";
import { UntypedFormArray, UntypedFormGroup } from "@angular/forms";

import { Environment, ENVIRONMENT_TOKEN } from "@intocare/environments";

import { filterOutErrors } from "../../utils";

@Directive({
	selector: "[icValidateForms]",
})
export class SubmitFormDirective {
	private readonly cdRef = inject(ChangeDetectorRef);
	private environment = inject<Environment>(ENVIRONMENT_TOKEN);

	private errors = [];
	private formInvalid = false;
	private errorsToIgnore = new Set<string>();

	// An array of forms that need to be valid in order to confirm
	@Input() icValidateForms: (UntypedFormGroup | UntypedFormArray)[] = [];
	// An array of conditions that all need to be true to confirm
	@Input() conditions: boolean[] = [];
	// Whether or not the confirm should be called when the form(s) is/are dirty
	@Input() onlyWhenDirty = true;
	// A list of errors we want to ignore
	@Input()
	set ignore(errors: string[]) {
		this.errorsToIgnore = new Set(errors);
	}

	@Output() confirm = new EventEmitter();
	@Output() invalidEvent = new EventEmitter();

	@HostListener("click")
	isClicked() {
		this.performAction();
	}

	@HostListener("keydown.enter")
	isEntered() {
		this.performAction();
	}

	private performAction() {
		this.formInvalid = false;

		let isDirty = false;
		for (let i = 0; i < this.icValidateForms.length; i++) {
			this.errors[i] = [];
			this.validateForm(this.icValidateForms[i], i, `${i}`);

			isDirty = isDirty || this.icValidateForms[i].dirty;
		}

		if (!this.isValid()) {
			if (!this.environment.production) {
				console.dir(this.errors);
			}

			this.invalidEvent.emit(this.errors);

			this.cdRef.detectChanges();

			return;
		}

		if (this.onlyWhenDirty && !isDirty) {
			return;
		}

		this.confirm.emit();
	}

	private isValid(): boolean {
		if (this.formInvalid) {
			return false;
		}

		let conditionsValid = true;

		if (this.conditions && this.conditions.length > 0) {
			conditionsValid = this.conditions.every(Boolean);
		}

		return conditionsValid;
	}

	private validateForm(formGroup: UntypedFormGroup | UntypedFormArray, formIndex: number, key: string) {
		if (!formGroup) {
			return;
		}

		const controls = Object.keys(formGroup.controls);
		const hasParent = Boolean(formGroup.parent);

		const groupErrors = filterOutErrors(formGroup, this.errorsToIgnore);

		if (groupErrors.length > 0) {
			this.formInvalid = true;

			this.errors[formIndex].push({
				key,
				errors: groupErrors,
			});
		}

		for (const controlKey of controls) {
			const control = formGroup.controls[controlKey];
			const controlErrors = filterOutErrors(control, this.errorsToIgnore);

			control.markAsTouched();

			if (controlErrors.length > 0) {
				this.formInvalid = true;

				this.errors[formIndex].push({
					key: `${hasParent ? `${key}.` : ""}${controlKey}`,
					errors: controlErrors,
				});
			}

			if ((control as UntypedFormGroup | UntypedFormArray).controls) {
				this.validateForm(control as UntypedFormGroup, formIndex, controlKey);
			}
		}
	}
}
